import "@shoelace-style/shoelace/dist/components/option/option.js";
import "@shoelace-style/shoelace/dist/components/select/select.js";

import { PropertyValues } from "@lit/reactive-element/reactive-element";
import { css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { map } from "lit/directives/map.js";
import { range } from "lit/directives/range.js";
import get from "lodash-es/get.js";
import set from "lodash-es/set.js";
import { PlaceTable } from "../backend/cloud/PlaceTable";
import {
    AllCaveRatings,
    AllDisabledAccessPotentials,
    AllExplorationPotentials,
    AllLocationTypes,
    AllTourismPotentials,
    AllTriState,
} from "../Filter";
import { IPlace } from "../models/Models";
import { BaseElement } from "./BaseElement";
import { Style } from "./Styles";

const PlaceColumns = [
    {
        property: "data.name",
        columnName: "Name",
        editorType: "stringEditor",
        options: [],
    },
    {
        property: "data.spelioData.type",
        columnName: "Place type",
        editorType: "enumPicker",
        options: AllLocationTypes,
    },
    {
        property: "data.locationKnown",
        columnName: "Location known",
        editorType: "booleanDropdown",
        options: [],
    },
    {
        property: "data.location.lat",
        columnName: "Latitude",
        editorType: "floatEditor",
        options: [],
    },
    {
        property: "data.location.lon",
        columnName: "Longitude",
        editorType: "floatEditor",
        options: [],
    },
    {
        property: "data.spelioData.explorationPotential",
        columnName: "Exploration",
        editorType: "enumPicker",
        options: AllExplorationPotentials,
    },
    {
        property: "data.spelioData.source",
        columnName: "Source",
        editorType: "stringEditor",
        options: [],
    },
    {
        property: "data.spelioData.reference",
        columnName: "Reference",
        editorType: "stringEditor",
        options: [],
    },
    {
        property: "data.spelioData.caveRating",
        columnName: "Cave rating",
        editorType: "enumPicker",
        options: AllCaveRatings,
    },
    {
        property: "data.spelioData.verticalAccess",
        columnName: "Vertical access",
        editorType: "enumPicker",
        options: AllTriState,
    },
    {
        property: "data.spelioData.tourismPotential",
        columnName: "Tourism",
        editorType: "enumPicker",
        options: AllTourismPotentials,
    },
    {
        property: "data.spelioData.disabledAccessPotential",
        columnName: "Disabled",
        editorType: "enumPicker",
        options: AllDisabledAccessPotentials,
    },

    // reference: string;
    // notes: string;
    // accessDetails: string;
    // files: string[];
    // labels: string[];
    // goesToWater: TriState | keyof typeof TriState;
    // references: Array<IReference>;
    // kml: string;
];

@customElement("ds-data-table")
export class DataTable extends BaseElement {
    static override styles = [
        Style,
        css`
            .pages {
                float: right;
                padding-right: 1rem;
            }

            .page {
                padding: 0.25rem;
                padding-left: 0.5rem;
                cursor: pointer;
            }

            .body {
                overflow: auto;
                width: 100%;
                height: 100%;
            }

            table {
                font-family: Arial, Helvetica, sans-serif;
                border-collapse: collapse;
                width: 100%;
            }

            td,
            th {
                border: 1px solid #ddd;
                padding: 8px;
            }

            tr:nth-child(even) {
                background-color: #101104;
            }

            tr:hover {
                background-color: #ddd;
            }

            th {
                padding-top: 12px;
                padding-bottom: 12px;
                text-align: left;
                background-color: var(--sl-primary-color-950);
                color: white;
            }
        `,
    ];

    @property({ type: Array }) columns = PlaceColumns;
    @property({ type: Array }) data: Array<IPlace> = [];
    @property({ type: Number }) pageSize = 50;
    @state() protected _numPages = 1;
    @state() protected _currentPage = 0;
    @state() protected _pageData: Array<IPlace> = [];

    private _places: PlaceTable;
    constructor() {
        super();
        this._places = this._kernel.dataStore.getSource<PlaceTable>("places");
    }

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._subscribeToKernelEvent("filter-changed", () => this._updateData());
        this._subscribeToKernelEvent("filtered-places-changed", () => this._updateData());
    }

    override async firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        await this._updateData();
    }

    private _showDeleteDialog(place: IPlace) {
        this._raiseEvent("ds-delete-place", {
            placeId: place.id,
        });
    }

    private async _updateData() {
        const filter = this._kernel.currentPlaceFilter.get();
        this.data = [...this._places.allFilteredItems(filter)];
        this._numPages = (this.data.length + this.pageSize) % this.pageSize;
        this._setPageData(this._currentPage);
    }

    private _renderEnumPicker(row: any, property: string, options: Array<string>) {
        return html` <sl-select
            hoist
            size="small"
            value=${get(row, property)}
            @sl-change=${(e: any) => {
                set(row, property, e.target.value);
                this._places.update(row as IPlace);
            }}
        >
            ${options.map((opt) => html`<sl-option value=${opt}>${opt}</sl-option>`)}
        </sl-select>`;
    }

    private _renderStringEditor(row: any, property: string) {
        return html`<sl-input
            size="small"
            value=${get(row, property)}
            @sl-change=${(e: any) => {
                set(row, property, e.target.value);
                this._places.update(row as IPlace);
            }}
        ></sl-input>`;
    }

    private _renderFloatEditor(row: any, property: string) {
        return html`<sl-input
            size="small"
            type="number"
            value=${get(row, property)}
            @sl-change=${(e: any) => {
                set(row, property, parseFloat(e.target.value));
                this._places.update(row as IPlace);
            }}
        ></sl-input>`;
    }

    private _renderBooleanDropdown(row: any, property: string) {
        return html` <sl-select
            hoist
            size="small"
            value=${get(row, property) ? "Yes" : "No"}
            @sl-change=${(e: any) => {
                set(row, property, e.target.value === "Yes");
                this._places.update(row as IPlace);
            }}
        >
            <sl-option value="No">No</sl-option>
            <sl-option value="Yes">Yes</sl-option>
        </sl-select>`;
    }

    private _setPageData(page: number) {
        this._currentPage = page;
        this._pageData = this.data.slice(
            this._currentPage * this.pageSize,
            this._currentPage * this.pageSize + this.pageSize
        );
    }

    override render() {
        return html` <div class="pages">
                ${map(
                    range(this._numPages),
                    (i) =>
                        html`<span
                            class="page"
                            style="font-weight:${this._currentPage === this._numPages - i - 1
                                ? "bold"
                                : "normal"}"
                            @click=${() => this._setPageData(this._numPages - i - 1)}
                            >${this._numPages - i}</span
                        >`
                )}
            </div>
            <div class="body">
                <table>
                    <tr>
                        <td></td>
                        ${this.columns.map((col) => html`<th>${col.columnName}</th>`)}
                    </tr>
                    ${this._pageData.map(
                        (row) => html`
                            <tr>
                                <td>
                                    <sl-icon-button
                                        name="trash3"
                                        @click=${() => this._showDeleteDialog(row)}
                                    ></sl-icon-button>
                                </td>
                                ${this.columns.map(
                                    (col) =>
                                        html`<td>
                                            ${choose(
                                                col.editorType,
                                                [
                                                    [
                                                        "enumPicker",
                                                        () =>
                                                            this._renderEnumPicker(
                                                                row,
                                                                col.property,
                                                                col.options
                                                            ),
                                                    ],
                                                    [
                                                        "stringEditor",
                                                        () =>
                                                            this._renderStringEditor(
                                                                row,
                                                                col.property
                                                            ),
                                                    ],
                                                    [
                                                        "floatEditor",
                                                        () =>
                                                            this._renderFloatEditor(
                                                                row,
                                                                col.property
                                                            ),
                                                    ],
                                                    [
                                                        "booleanDropdown",
                                                        () =>
                                                            this._renderBooleanDropdown(
                                                                row,
                                                                col.property
                                                            ),
                                                    ],
                                                ],
                                                () => html`${get(row, col.property)}`
                                            )}
                                        </td>`
                                )}
                            </tr>
                        `
                    )}
                </table>
            </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-data-table": DataTable;
    }
}
